import { useEffect, useState, useCallback, useMemo } from "react";
import MenuIcon from '@mui/icons-material/Menu';
import { Divider, Drawer, Grid, List, ListItem, ListItemText, useMediaQuery,Tooltip,Container, MenuItem, Menu, Typography,IconButton,Toolbar,Box,AppBar } from '@mui/material';
import Logo from "../../assets/currentumlogo.png";
import Language from "../Language/index";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react"; 
import { getIdToken, loginRequest, msalResult } from "../../authConfig";
import { callMsGraph } from "../../graph";
import { CloseOutlined, Inventory2Outlined } from "@mui/icons-material";
import { GetUsersIdentity } from "../../services/ApiService";
import clsx from "clsx";
import { useStyle } from "../../styles/style";
import { safeString, hasValidString } from "../../utils/stringUtils";
import { logger } from "../../utils/logger";

type UserProfileData = {
  displayName?: string;
  surname?: string;
};

function HeaderNavigation() {
  // State
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfileData>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showAccessPackageButton, setShowAccessPackageButton] = useState(false);
  
  // Hooks
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:768px)');
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = msalResult;
  const { classes } = useStyle();

  // Event handlers
  const openMobileMenu = useCallback(() => {
    setMobileDrawerOpen(true);
  }, []);

  const openUserMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  const closeUserMenu = useCallback(() => {
    setUserMenuAnchor(null);
  }, []);

  const closeMobileDrawer = useCallback((event: React.KeyboardEvent | React.MouseEvent) => {
    if (event?.type === 'keydown' && 
        ((event as React.KeyboardEvent).key === 'Tab' || 
         (event as React.KeyboardEvent).key === 'Shift')) {
      return;
    }
    setMobileDrawerOpen(false);
  }, []);

  const navigateToAccessPackages = useCallback(() => {
    navigate('/access-packages');
    closeMobileMenu();
  }, [navigate, closeMobileMenu]);

  const handleLogout = useCallback((logoutType: "redirect" | "popup" = "redirect") => {
    if (logoutType === "popup") {
      instance.logoutPopup({ mainWindowRedirectUri: "/" });
    } else {
      instance.logoutRedirect();
    }
  }, [instance]);

  // Data fetching functions
  const fetchUserProfile = useCallback(() => {
    if (!accounts[0]) return;
    
    instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] })
      .then(response => callMsGraph(response.accessToken)
        .then(userData => setUserProfile(userData))
        .catch((error) => logger.error('Graph API error:', error))
      )
      .catch((error) => logger.error('Token acquisition error:', error));
  }, [instance, accounts]);

  const fetchUserRights = useCallback(async () => {
    try {
      const token = await getIdToken();
      const response = await GetUsersIdentity(token);
      setIsAdmin(response?.data?.isAdmin === true);
    } catch (error) {
      logger.error('Error fetching user rights:', error);
    }
  }, []);

  // Effects
  useEffect(() => {
    setShowAccessPackageButton(location.pathname === "/access-packages");
  }, [location.pathname]);

  useEffect(() => { 
    fetchUserProfile();
    fetchUserRights();
  }, [fetchUserProfile, fetchUserRights]);

  // Computed values
  const userDisplayName = useMemo(() => {
    const role = isAdmin ? 'Admin' : 'User';
    const name = userProfile?.displayName || userProfile?.surname || '';
    return `${role} | ${name}`;
  }, [isAdmin, userProfile]);

  const drawerClassNames = useMemo(() => 
    clsx((!isMobile && !isTablet) 
      ? classes.drawerForm 
      : (isTablet && !isMobile) 
        ? classes.drawerMenuTablet 
        : classes.drawerFormMobile
    ), 
  [classes, isMobile, isTablet]);

  return (
    <AppBar 
      position="static" 
      sx={{ backgroundColor: safeString(theme?.Style?.headerBg) || '#1D7058' }} 
      elevation={0}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{display: 'flex', justifyContent: 'space-between', width: '100%', minHeight: '80px !important'}}>
          {/* Logo */}
          <Grid display="flex" justifyContent="start" item style={{maxWidth: '100%', height: 'auto', cursor: 'pointer'}}>
            <img  
              onClick={() => navigate("/")} 
              src={hasValidString(theme?.FileAccess?.baseUrl) && hasValidString(theme?.LandingPage?.logoUrl) && hasValidString(theme?.FileAccess?.sasToken)
                ? `${safeString(theme?.FileAccess?.baseUrl)}${safeString(theme?.LandingPage?.logoUrl)}?${safeString(theme?.FileAccess?.sasToken)}`
                : Logo
              } 
              alt="company logo" 
              style={{ maxWidth: safeString(theme?.Style?.maxWidth) || '200px', height: '100%', objectFit: 'contain' }}
            />
          </Grid>

          {/* Navigation */}
          <Grid item sx={{display: 'flex'}}>
            {/* Mobile menu button */}
            <Box sx={{flexGrow: 1, display: {xs: 'flex', md: 'none'}}}>
              <IconButton 
                size="large" 
                aria-label="account menu" 
                aria-controls="mobile-menu" 
                aria-haspopup="true" 
                onClick={openMobileMenu} 
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Desktop navigation */}
            <Box sx={{flexGrow: 1, paddingRight: '16px', display: {xs: 'none', md: 'flex'}}}>
              {isAdmin && !showAccessPackageButton && (
                <button 
                  onClick={navigateToAccessPackages}
                  style={{
                    marginRight: "32px", 
                    cursor: "pointer", 
                    backgroundColor: safeString(theme?.Style?.btnColor) || '#09b885', 
                    padding: "8px 16px", 
                    color: "white", 
                    fontWeight: '400', 
                    borderRadius: "15px", 
                    fontFamily: safeString(theme?.Style?.font) || 'Arial, sans-serif', 
                    fontSize: '12px', 
                    textTransform: "capitalize"
                  }}
                >
                  Access Packages
                </button>
              )}
              
              {isAdmin && showAccessPackageButton && (
                <button 
                  onClick={() => navigate('/')}
                  style={{
                    marginRight: "32px", 
                    cursor: "pointer", 
                    backgroundColor: safeString(theme?.Style?.btnColor) || '#09b885', 
                    padding: "8px 16px", 
                    color: "white", 
                    fontWeight: '400', 
                    borderRadius: "15px", 
                    fontFamily: safeString(theme?.Style?.font) || 'Arial, sans-serif', 
                    fontSize: '12px', 
                    textTransform: "capitalize"
                  }}
                >
                  Application List
                </button>
              )}
              
              {/* User profile icon and menu */}
              <Tooltip title="User menu">
                <IconButton onClick={openUserMenu} sx={{p: 0}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="33" height="34" viewBox="0 0 43 44">
                    <defs>
                      <clipPath id="clip-path">
                        <rect id="Rectangle_48" data-name="Rectangle 48" width="31" height="31" transform="translate(-0.037 -0.037)" fill="#fff"/>
                      </clipPath>
                    </defs>
                    <g id="Group_924" data-name="Group 924" transform="translate(-1570 -33)">
                      <rect id="Rectangle_56" data-name="Rectangle 56" width="43" height="44" rx="10" transform="translate(1570 33)" fill={theme?.Style?.btnColor}/>
                      <g id="Group_147" data-name="Group 147" transform="translate(1578.037 42.037)">
                        <g id="Group_138" data-name="Group 138" transform="translate(-2 -2)" clipPath="url(#clip-path)">
                          <path id="Path_137" data-name="Path 137" d="M83.842,53.95a4.923,4.923,0,1,1,4.923-4.923,4.928,4.928,0,0,1-4.923,4.923m0-8.605a3.682,3.682,0,1,0,3.682,3.682,3.687,3.687,0,0,0-3.682-3.682" transform="translate(-68.287 -37.071)" fill="#fff"/>
                          <path id="Path_138" data-name="Path 138" d="M13.082,26.162A13.081,13.081,0,1,1,26.162,13.081,13.1,13.1,0,0,1,13.082,26.162m0-24.921a11.84,11.84,0,1,0,11.84,11.84A11.854,11.854,0,0,0,13.082,1.24" transform="translate(2.473 2.474)" fill="#fff"/>
                          <path id="Path_139" data-name="Path 139" d="M44.244,160.469a12.986,12.986,0,0,1-9.309-3.894.62.62,0,0,1-.174-.514,7.189,7.189,0,0,1,7.118-6.27h4.6a7.166,7.166,0,0,1,7.136,6.392.62.62,0,0,1-.181.509,13,13,0,0,1-9.192,3.777m-8.207-4.548a11.835,11.835,0,0,0,16.307.1,5.927,5.927,0,0,0-5.863-4.99h-4.6a5.947,5.947,0,0,0-5.842,4.889" transform="translate(-28.689 -131.834)" fill="#fff"/>
                        </g>
                      </g>
                    </g>
                  </svg>
                  <Typography 
                    variant="subtitle1" 
                    style={{
                      color: '#fff', 
                      marginRight: 30, 
                      paddingLeft: 10, 
                      fontFamily: theme?.Style?.font, 
                      fontSize: '16px', 
                      fontWeight: '500'
                    }}
                  >
                    {userDisplayName}
                  </Typography>
                </IconButton> 
              </Tooltip>

              {/* User menu dropdown */}
              <Menu 
                id="user-menu"
                anchorEl={userMenuAnchor}
                keepMounted
                open={Boolean(userMenuAnchor)}
                onClose={closeUserMenu}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                sx={{mt: '45px'}}
              >
                <MenuItem 
                  onClick={() => handleLogout()} 
                  sx={{padding: '10px 24px', fontFamily: theme?.Style?.font, fontSize: '12px'}}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 43 44" style={{marginRight: '8px'}}>
                    <defs>
                      <clipPath id="clip-path">
                        <rect id="Rectangle_48" data-name="Rectangle 48" width="31" height="31" transform="translate(-0.037 -0.037)" fill="#fff"/>
                      </clipPath>
                    </defs>
                    <g id="Group_924" data-name="Group 924" transform="translate(-1570 -33)">
                      <rect id="Rectangle_56" data-name="Rectangle 56" width="43" height="44" rx="10" transform="translate(1570 33)" fill={theme?.Style?.btnColor}/>
                      <g id="Group_147" data-name="Group 147" transform="translate(1578.037 42.037)">
                        <g id="Group_138" data-name="Group 138" transform="translate(-2 -2)" clipPath="url(#clip-path)">
                          <path id="Path_137" data-name="Path 137" d="M83.842,53.95a4.923,4.923,0,1,1,4.923-4.923,4.928,4.928,0,0,1-4.923,4.923m0-8.605a3.682,3.682,0,1,0,3.682,3.682,3.687,3.687,0,0,0-3.682-3.682" transform="translate(-68.287 -37.071)" fill="#fff"/>
                          <path id="Path_138" data-name="Path 138" d="M13.082,26.162A13.081,13.081,0,1,1,26.162,13.081,13.1,13.1,0,0,1,13.082,26.162m0-24.921a11.84,11.84,0,1,0,11.84,11.84A11.854,11.854,0,0,0,13.082,1.24" transform="translate(2.473 2.474)" fill="#fff"/>
                          <path id="Path_139" data-name="Path 139" d="M44.244,160.469a12.986,12.986,0,0,1-9.309-3.894.62.62,0,0,1-.174-.514,7.189,7.189,0,0,1,7.118-6.27h4.6a7.166,7.166,0,0,1,7.136,6.392.62.62,0,0,1-.181.509,13,13,0,0,1-9.192,3.777m-8.207-4.548a11.835,11.835,0,0,0,16.307.1,5.927,5.927,0,0,0-5.863-4.99h-4.6a5.947,5.947,0,0,0-5.842,4.889" transform="translate(-28.689 -131.834)" fill="#fff"/>
                        </g>
                      </g>
                    </g>
                  </svg>
                  Log out
                </MenuItem>
              </Menu>
            </Box>
            <Language/>
            <Grid item>
              <Drawer
                variant="temporary"
                anchor="right"
                open={isMobileDrawerOpen}
                onClose={closeMobileDrawer}
                classes={{paper: drawerClassNames}}
                sx={{
                  display: {xs: 'flex', sm: 'flex', md: 'flex', width: '50px'},
                  '& .MuiDrawer-paper': {boxSizing: 'border-box'},
                }}
              >
                <Grid container justifyContent="space-between" display="flex" padding="8px">
                  <Grid item paddingRight="8px" paddingTop="8px">
                    <div>{userProfile?.displayName}</div>
                  </Grid>
                  <Grid item paddingRight="8px" paddingTop="8px" onClick={closeMobileMenu} style={{cursor: 'pointer'}}>
                    <CloseOutlined/>
                  </Grid>
                </Grid>
                <Divider sx={{my: 2}}/>
                <List>
                  <ListItem sx={{display: 'flex', flexDirection: 'column', justifyContent: 'start'}}>
                    {isAdmin && (
                      <ListItemText sx={{width: '100%', display: 'flex !important'}}>
                        <div style={{display: 'flex'}}>
                          <Inventory2Outlined sx={{color: 'lightgreen', marginLeft: '20px'}}/>
                          {!showAccessPackageButton ? (
                            <MenuItem 
                              onClick={() => navigate("/access-packages")} 
                              sx={{padding: '0px 24px', fontFamily: theme?.Style?.font, fontSize: '12px'}}
                            >
                              Access Package
                            </MenuItem>
                          ) : (
                            <MenuItem 
                              onClick={() => navigate("/")} 
                              sx={{padding: '0px 24px', fontFamily: theme?.Style?.font, fontSize: '12px'}}
                            >
                              Application Lists
                            </MenuItem>
                          )}
                        </div>
                      </ListItemText>
                    )}
                    
                    <ListItemText sx={{width: '100%'}}> 
                      <MenuItem 
                        onClick={() => handleLogout()} 
                        sx={{padding: '10px 24px', fontFamily: theme?.Style?.font, fontSize: '12px'}}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 43 44" style={{marginRight: '8px'}}>
                          <defs>
                            <clipPath id="clip-path">
                              <rect id="Rectangle_48" data-name="Rectangle 48" width="31" height="31" transform="translate(-0.037 -0.037)" fill="#fff"/>
                            </clipPath>
                          </defs>
                          <g id="Group_924" data-name="Group 924" transform="translate(-1570 -33)">
                            <rect id="Rectangle_56" data-name="Rectangle 56" width="43" height="44" rx="10" transform="translate(1570 33)" fill={theme?.Style?.btnColor}/>
                            <g id="Group_147" data-name="Group 147" transform="translate(1578.037 42.037)">
                              <g id="Group_138" data-name="Group 138" transform="translate(-2 -2)" clipPath="url(#clip-path)">
                                <path id="Path_137" data-name="Path 137" d="M83.842,53.95a4.923,4.923,0,1,1,4.923-4.923,4.928,4.928,0,0,1-4.923,4.923m0-8.605a3.682,3.682,0,1,0,3.682,3.682,3.687,3.687,0,0,0-3.682-3.682" transform="translate(-68.287 -37.071)" fill="#fff"/>
                                <path id="Path_138" data-name="Path 138" d="M13.082,26.162A13.081,13.081,0,1,1,26.162,13.081,13.1,13.1,0,0,1,13.082,26.162m0-24.921a11.84,11.84,0,1,0,11.84,11.84A11.854,11.854,0,0,0,13.082,1.24" transform="translate(2.473 2.474)" fill="#fff"/>
                                <path id="Path_139" data-name="Path 139" d="M44.244,160.469a12.986,12.986,0,0,1-9.309-3.894.62.62,0,0,1-.174-.514,7.189,7.189,0,0,1,7.118-6.27h4.6a7.166,7.166,0,0,1,7.136,6.392.62.62,0,0,1-.181.509,13,13,0,0,1-9.192,3.777m-8.207-4.548a11.835,11.835,0,0,0,16.307.1,5.927,5.927,0,0,0-5.863-4.99h-4.6a5.947,5.947,0,0,0-5.842,4.889" transform="translate(-28.689 -131.834)" fill="#fff"/>
                              </g>
                            </g>
                          </g>
                        </svg>
                        Log out
                      </MenuItem>
                    </ListItemText>
                  </ListItem>
                </List>
              </Drawer>
            </Grid>
          </Grid>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default HeaderNavigation;
